"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
    Shield,
    LayoutDashboard,
    Users,
    FolderTree,
    Settings,
    Menu,
    LogOut,
    User,
    Bell,
} from "lucide-react";
import { FontSizeToggle } from "@/components/a11y/font-size-toggle";

const navigation = [
    { name: "แดชบอร์ด", href: "/admin/dashboard", icon: LayoutDashboard },
    { name: "ผู้ใช้งาน", href: "/admin/users", icon: Users },
    { name: "ประเภทเรื่อง", href: "/admin/categories", icon: FolderTree },
    { name: "ตั้งค่าระบบ", href: "/admin/settings", icon: Settings },
];

export default function AdminLayout({
    children,
}: {
    children: React.ReactNode;
}) {
    const pathname = usePathname();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const Sidebar = ({ mobile = false }: { mobile?: boolean }) => (
        <div className={`flex h-full flex-col ${mobile ? "" : "w-64"}`}>
            {/* Logo */}
            <div className="flex h-16 items-center gap-3 px-4 border-b border-sidebar-border">
                <Image
                    src="/logo_cus.png"
                    alt="กรมศุลกากร"
                    width={40}
                    height={40}
                    className="h-10 w-10 object-contain"
                />
                <div className="flex flex-col">
                    <span className="text-sm font-semibold text-sidebar-foreground">กรมศุลกากร</span>
                    <span className="text-xs text-sidebar-foreground/70">Admin Portal</span>
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1">
                {navigation.map((item) => {
                    const isActive = pathname.startsWith(item.href);
                    return (
                        <Link
                            key={item.name}
                            href={item.href}
                            onClick={() => mobile && setSidebarOpen(false)}
                            className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive
                                ? "bg-sidebar-accent text-sidebar-accent-foreground"
                                : "text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                                }`}
                        >
                            <item.icon className="h-5 w-5" />
                            {item.name}
                        </Link>
                    );
                })}
            </nav>

            {/* User section */}
            <div className="p-4 border-t border-sidebar-border">
                <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9">
                        <AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground text-sm">
                            สบ
                        </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-sidebar-foreground truncate">
                            นายสมศักดิ์ บริหาร
                        </p>
                        <p className="text-xs text-sidebar-foreground/70">ผู้ดูแลระบบ</p>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-muted/30">
            {/* Mobile Sidebar */}
            <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
                <SheetContent side="left" className="p-0 w-64 bg-sidebar">
                    <Sidebar mobile />
                </SheetContent>
            </Sheet>

            {/* Desktop Sidebar */}
            <aside className="fixed inset-y-0 left-0 z-40 hidden md:block w-64 bg-sidebar border-r border-sidebar-border">
                <Sidebar />
            </aside>

            {/* Main Content */}
            <div className="md:pl-64">
                {/* Top bar */}
                <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b bg-white px-4 md:px-6">
                    <Sheet>
                        <SheetTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="md:hidden"
                                onClick={() => setSidebarOpen(true)}
                                aria-label="เปิดเมนู"
                            >
                                <Menu className="h-5 w-5" />
                            </Button>
                        </SheetTrigger>
                    </Sheet>

                    <div className="flex-1" />

                    <div className="flex items-center gap-2">
                        <FontSizeToggle variant="compact" />
                        <Button variant="ghost" size="icon" className="relative" aria-label="แจ้งเตือน">
                            <Bell className="h-5 w-5" />
                            <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-[10px] font-medium text-white flex items-center justify-center">
                                5
                            </span>
                        </Button>

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                    <Avatar className="h-8 w-8">
                                        <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                                            สบ
                                        </AvatarFallback>
                                    </Avatar>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel>
                                    <div className="flex flex-col">
                                        <span>นายสมศักดิ์ บริหาร</span>
                                        <span className="text-xs font-normal text-muted-foreground">
                                            admin@customs.go.th
                                        </span>
                                    </div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem>
                                    <User className="mr-2 h-4 w-4" />
                                    โปรไฟล์
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem asChild>
                                    <Link href="/" className="text-destructive">
                                        <LogOut className="mr-2 h-4 w-4" />
                                        ออกจากระบบ
                                    </Link>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                <main className="p-4 md:p-6">{children}</main>
            </div>
        </div>
    );
}
